// The game's marks: one source of the geometry, two backends that draw it
// (R230, ui-map-interface.md §6.1 and §6.2).
//
// A mark is the shape of one thing on the map — a hold, a deposit, a control
// point, a hull silhouette. The map bakes every mark into the atlas the
// engine draws from. The livery preview, the badges, the terrain lab and the
// symbols page draw the same marks on a plain 2D canvas. Before this file
// the two sides held two different pictures of the same objects.
//
// This file holds the geometry one time. A description takes a
// `MarkGraphics`, the small vector API the descriptions use. The shape of it
// is the `GraphicsContext` API of PixiJS v8, the renderer this file outlived
// (R505); every mention of that name below states where a rule came from.
// `Canvas2DMarks` satisfies it over a `CanvasRenderingContext2D`, and the
// atlas builder satisfies it over its own baker.
//
// **The rule of a description**: a hull description lays down paths and never
// calls `fill()` or `stroke()`. The master builder decides the ink, which is
// how one description gives both the filled master and the hollow one (card
// B05). The site descriptions do paint, because a site mark carries several
// colours and no caller chooses them.
//
// **The path rule** (PixiJS v8, and `Canvas2DMarks` copies it): path calls
// accumulate. `fill()` paints every sub-path since the last paint and then
// starts a new path, and `stroke()` does the same. A shape that must be
// filled and then outlined is therefore declared two times — see
// `drawDeposit` here and `fillStroke` of `hold-marks.ts`.

import { resourceOf } from '../world';
import { DEPOSIT_LEVELS } from '../gen/balance';
import { DEPOSIT_MARKS } from '../gen/presentation';
import type { DepositStyle } from '../ui/settings';
import { ShipClass } from '../gen/proto/aquaphobia';
import { mixColor, rgba } from './color';

/** The corner style of a stroke. The three values the API accepts. */
export type MarkJoin = 'round' | 'bevel' | 'miter';

/** The end style of a stroke. The three values the API accepts. */
export type MarkCap = 'butt' | 'round' | 'square';

/** A fill. `color` is a 24-bit RGB number; `alpha` is 1 when it is absent. */
export interface MarkFill {
  color: number;
  alpha?: number;
}

/** A stroke. `color` is a 24-bit RGB number; `alpha` is 1 when it is absent. */
export interface MarkStroke {
  width: number;
  color: number;
  alpha?: number;
  join?: MarkJoin;
  cap?: MarkCap;
}

/**
 * What a mark description may ask of the surface it draws on.
 *
 * It is the subset of the `GraphicsContext` API that the
 * descriptions below use, and nothing more. Every method returns `this`,
 * because the descriptions chain.
 *
 * `GraphicsContext` and `Graphics` satisfy this interface structurally, so
 * `sites.ts` and `fleets.ts` pass their objects with no cast. A parameter
 * type here must therefore stay at least as wide as that signature.
 */
export interface MarkGraphics {
  moveTo(x: number, y: number): this;
  lineTo(x: number, y: number): this;
  arc(
    x: number, y: number, radius: number,
    startAngle: number, endAngle: number, counterclockwise?: boolean,
  ): this;
  closePath(): this;
  /** A polygon. It closes when `close` is absent, which is the PixiJS rule. */
  poly(points: number[], close?: boolean): this;
  circle(x: number, y: number, radius: number): this;
  fill(style: MarkFill): this;
  stroke(style: MarkStroke): this;
}

/**
 * A `MarkGraphics` over a plain 2D canvas, for a caller with no renderer.
 *
 * It copies the PixiJS v8 path semantics the descriptions depend on:
 *
 * 1. Path calls accumulate. `fill()` paints every sub-path since the last
 *    paint, then starts a new path; `stroke()` does the same.
 * 2. A paint does not lose the current point. PixiJS starts the next path
 *    with a `moveTo` to the last point the painted path reached, so an
 *    `arc()` that follows a paint joins to it. A description that must not
 *    join states its own `moveTo` first, which is what every arc of
 *    `hold-marks.ts` does.
 * 3. `arc()` from a current point draws a line to the start of the arc, which
 *    is what a 2D canvas already does. `drawDepositGauge` and every arc of
 *    `hold-marks.ts` call `moveTo` first for exactly this reason, and the
 *    shim must not correct it.
 * 4. `circle()` is a shape of its own and leaves no current point behind.
 *
 * The shim touches no transform and saves and restores nothing: the caller
 * places and rotates the context. Alpha rides in the colour string rather
 * than in `globalAlpha`, so a caller that fades a whole scene keeps its fade.
 *
 * Deviation, recorded so nobody hunts it: after an `arc()` or a `circle()`
 * PixiJS reads a last point from the wrong fields of its own instruction and
 * carries a junk coordinate into the next path. The junk starts a degenerate
 * segment that the map never shows. This shim carries the true end point of
 * an arc, and no point at all after a circle.
 */
export class Canvas2DMarks implements MarkGraphics {
  private lastX = 0;
  private lastY = 0;
  /** Does a current point survive into the path after the next paint? */
  private carry = false;

  /**
   * `tint` replaces the colour of every paint while it is set.
   *
   * The hull masters are drawn white and are tinted per faction by the sprite
   * that carries them (`hullTint` in `fleets.ts`). A 2D caller has no sprite,
   * so it asks for the faction colour here instead. Set it to `null` for a
   * mark that carries its own colours, such as a hold symbol.
   */
  constructor(
    private readonly ctx: CanvasRenderingContext2D,
    public tint: number | null = null,
  ) {}

  /** The colour a paint uses: the tint while one is set, else the mark's. */
  private ink(color: number): number {
    return this.tint ?? color;
  }

  /** Ends the painted path and re-opens the one PixiJS would leave behind. */
  private restart(): void {
    this.ctx.beginPath();
    if (this.carry) this.ctx.moveTo(this.lastX, this.lastY);
  }

  private mark(x: number, y: number): void {
    this.lastX = x;
    this.lastY = y;
    this.carry = true;
  }

  moveTo(x: number, y: number): this {
    this.ctx.moveTo(x, y);
    this.mark(x, y);
    return this;
  }

  lineTo(x: number, y: number): this {
    this.ctx.lineTo(x, y);
    this.mark(x, y);
    return this;
  }

  arc(
    x: number, y: number, radius: number,
    startAngle: number, endAngle: number, counterclockwise = false,
  ): this {
    this.ctx.arc(x, y, radius, startAngle, endAngle, counterclockwise);
    this.mark(x + Math.cos(endAngle) * radius, y + Math.sin(endAngle) * radius);
    return this;
  }

  /** Closes the sub-path. The current point stays where it was (PixiJS). */
  closePath(): this {
    this.ctx.closePath();
    return this;
  }

  poly(points: number[], close = true): this {
    if (points.length < 2) return this;
    this.ctx.moveTo(points[0], points[1]);
    for (let i = 2; i < points.length; i += 2) this.ctx.lineTo(points[i], points[i + 1]);
    if (close) this.ctx.closePath();
    this.mark(points[points.length - 2], points[points.length - 1]);
    return this;
  }

  circle(x: number, y: number, radius: number): this {
    // The `moveTo` keeps the circle a sub-path of its own, the way a PixiJS
    // shape primitive is. Without it the canvas joins it to the path before.
    this.ctx.moveTo(x + radius, y);
    this.ctx.arc(x, y, radius, 0, Math.PI * 2, false);
    this.carry = false;
    return this;
  }

  fill(style: MarkFill): this {
    this.ctx.fillStyle = rgba(this.ink(style.color), style.alpha ?? 1);
    this.ctx.fill();
    this.restart();
    return this;
  }

  stroke(style: MarkStroke): this {
    this.ctx.lineWidth = style.width;
    this.ctx.strokeStyle = rgba(this.ink(style.color), style.alpha ?? 1);
    // Both are stated every time. A stroke that named neither must not
    // inherit the corner of the stroke before it.
    this.ctx.lineJoin = style.join ?? 'miter';
    this.ctx.lineCap = style.cap ?? 'butt';
    this.ctx.stroke();
    this.restart();
    return this;
  }
}

/** The box a drawing covers, in its own units. */
export interface MarkBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/**
 * A `MarkGraphics` that draws nothing and keeps the box of everything it
 * was asked to draw. An arc counts its whole circle, which is never smaller
 * than the arc. `box` is the box of the path points; `result()` grows it by
 * half the widest stroke. The squad key sizes its marks with it, and the
 * livery reads the half span of a hull off it.
 */
export class BoxProbe implements MarkGraphics {
  readonly box: MarkBox = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity };
  private pad = 0;

  private take(x: number, y: number, r = 0): void {
    const b = this.box;
    b.x0 = Math.min(b.x0, x - r); b.y0 = Math.min(b.y0, y - r);
    b.x1 = Math.max(b.x1, x + r); b.y1 = Math.max(b.y1, y + r);
  }

  moveTo(x: number, y: number): this { this.take(x, y); return this; }
  lineTo(x: number, y: number): this { this.take(x, y); return this; }
  arc(x: number, y: number, r: number): this { this.take(x, y, r); return this; }
  closePath(): this { return this; }
  poly(points: number[]): this {
    for (let i = 0; i + 1 < points.length; i += 2) this.take(points[i]!, points[i + 1]!);
    return this;
  }
  circle(x: number, y: number, r: number): this { this.take(x, y, r); return this; }
  fill(): this { return this; }
  stroke(style: MarkStroke): this { this.pad = Math.max(this.pad, style.width / 2); return this; }

  /** The box grown by the widest stroke, or null when nothing was drawn. */
  result(): MarkBox | null {
    const b = this.box;
    if (!(b.x1 >= b.x0)) return null;
    return { x0: b.x0 - this.pad, y0: b.y0 - this.pad, x1: b.x1 + this.pad, y1: b.y1 + this.pad };
  }
}

// ---------------------------------------------------------------------------
// Site marks (ui-map-interface.md §6.2)
// ---------------------------------------------------------------------------

export function drawCP(g: MarkGraphics, color: number) {
  g.circle(0, 0, 7).fill({ color, alpha: 0.9 });
  g.circle(0, 0, 12).stroke({ width: 2, color, alpha: 0.7 });
}

/**
 * The ground plate: a hexagonal plinth under the hold's symbol, as though
 * the sea floor were cut for the hold (cards C01 and D03). The viewer's own hold
 * gets the full plate — wider, with a bright white rim — and a clanmate's
 * gets the same shape with the rim removed. The shape carries the family
 * and the missing rim carries the rank.
 *
 * It is static. Nothing breathes, nothing rotates, and `tick()` never
 * touches it, which is most of the point of the pick.
 *
 * The hexagon is squashed to half height, so the plinth lies on the floor in
 * the same false perspective the dome and its foundation already use. The
 * points carry the squash rather than a scale on the Graphics, because a
 * scaled Graphics squashes the rim stroke with them and gives a rim that
 * changes width around the shape.
 */
export function drawPlate(g: MarkGraphics, color: number, mine: boolean) {
  const r = mine ? 27 : 25;
  const pts: number[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    // y 11 is the mockup's plate centre at y 7 plus the 4 px by which this
    // client's dome sits lower than the mockup's.
    pts.push(Math.cos(a) * r, 11 + Math.sin(a) * r * 0.5);
  }
  g.poly(pts).fill({ color, alpha: mine ? 0.16 : 0.20 });
  if (mine) g.poly(pts).stroke({ width: 1.5, color: 0xffffff, alpha: 0.7 });
}

// The deposit diamond, clockwise from its top point. The stock fills it from
// the bottom point up (R475).
const DIAMOND = [0, -7, 5, 0, 0, 7, -5, 0];

// The satellite chips. The count says the commodity without colour: one
// chip for alloy, two for biomass, three for volatiles (§2, §6.2).
const DEPOSIT_CHIPS = [
  [7, -3, 11, 1, 7, 5, 4, 1],
  [-7, -2, -4, 2, -7, 6, -10, 2],
  [0, -13, 3, -10, 0, -7, -3, -10],
];

/**
 * The part of the diamond at or below `yCut`, as a polygon (R475).
 *
 * The fill of a stocked field rises from the bottom point, and PixiJS has no
 * clip inside a shared `GraphicsContext`, so the cut is computed instead of
 * masked. The diamond is convex, so one half-plane leaves one convex
 * polygon. It has four shapes:
 *
 * | `yCut`         | Vertices | The shape                          |
 * | -------------- | -------- | ---------------------------------- |
 * | at or below -7 |        4 | the whole diamond                  |
 * | -7 to 0        |        5 | the cut crosses the two top edges  |
 * | 0 to 7         |        3 | the cut crosses the two foot edges |
 * | at or above 7  |        0 | nothing is left                    |
 *
 * The half width of the diamond at `y` is `5·(7−|y|)/7`.
 */
export function diamondBelow(yCut: number): number[] {
  if (yCut <= -7) return [...DIAMOND];
  if (yCut >= 7) return [];
  if (yCut < 0) {
    const w = 5 * (yCut + 7) / 7;
    return [-w, yCut, w, yCut, 5, 0, 0, 7, -5, 0];
  }
  const w = 5 * (7 - yCut) / 7;
  return [-w, yCut, w, yCut, 0, 7];
}

/**
 * The fill step a field of `amount` out of `capacity` draws at (R475).
 *
 * Zero is the worked-out field. Any stock at all keeps at least one step, so
 * a field the eye can still see is a field a hauler can still work. A
 * capacity of zero, which is a server or a snapshot older than R475, and a
 * capacity below the amount, both draw the field full.
 */
export function depositLevel(amount: number, capacity: number): number {
  if (!(amount > 0)) return 0;
  // The test is written this way round, so a capacity that is absent from an
  // old snapshot, and one that is not a number at all, both draw full.
  if (!(capacity > 0) || capacity < amount) return DEPOSIT_LEVELS;
  const step = Math.ceil(DEPOSIT_LEVELS * amount / capacity);
  return Math.min(DEPOSIT_LEVELS, Math.max(1, step));
}

/**
 * A field, and how much is left in it (R475, mockup round twenty-five card
 * 04 "Drain").
 *
 * The colour and the chip count say the commodity. The fill of the diamond
 * says the stock: it stands at `level / DEPOSIT_LEVELS` of the height, and
 * it rises from the bottom point. The fill against the bare rim says the
 * state, so nothing here is said with alpha and a sovereignty tint under
 * the mark takes nothing away.
 *
 * A worked-out field (`level` 0) is the rim and the chips as hollow
 * outlines. It keeps its chip count, so the commodity still reads when the
 * colour is gone (R475).
 *
 * Shape stays constant, so a deposit still reads as a deposit at any zoom.
 */
export function drawDeposit(g: MarkGraphics, resource: number, level: number) {
  const c = depositInk(resource);
  const stocked = level > 0;
  if (stocked) {
    // A stocked level is one step at least, so the cut is never empty.
    const step = Math.min(level, DEPOSIT_LEVELS) / DEPOSIT_LEVELS;
    g.poly(diamondBelow(7 - 14 * step)).fill({ color: c, alpha: 1 });
  }
  // The chips are one paint, not one paint each (§10.1). They stay solid at
  // every stocked level, because the count is the commodity and a faded
  // count at a low level would lose it (R475).
  chipPaths(g, resource);
  if (stocked) g.fill({ color: c, alpha: 0.9 });
  else g.stroke({ width: DEPOSIT_CHIP_W, color: c, alpha: 0.35 });
  g.poly(DIAMOND).stroke({
    width: DEPOSIT_RIM_W, color: brighten70(c), alpha: stocked ? 0.95 : 0.35,
  });
}

// ---------------------------------------------------------------------------
// The five alternative deposit marks (themes.md §3.5, §6.2, R832)
// ---------------------------------------------------------------------------
//
// The `deposit` field of a Marks body picks the mark a field wears. The
// AUTO value `drain` is `drawDeposit` above; the five below come from
// mockup round twenty-five, cards 03, 05, 06, 08 and 10.
//
// A value REPLACES the drain, and never stacks on it. Every figure comes
// from `DEPOSIT_MARKS`, and the shapes are the two the AUTO mark already
// draws: the diamond and the chips.
//
// Two parts of a card the mark interface cannot draw ride in the atlas
// instead (`fast/gfx/atlas.ts`): the `ghost` halo, which is a radial
// gradient, and the `tag` letter, which is text. Both are baked into the
// same cell, before and after the description below.
//
// The mockup's shimmer is off under every value. The atlas bakes a still
// cell, and R475 took the shimmer out of the drain for the same reason: a
// deposit says the stock and the state with shape, and neither with alpha.

/** The stroke of a chip outline and of a tile rim, in map px (R475). */
const DEPOSIT_CHIP_W = 1;
/** The stroke of the diamond's rim, in map px (R475). */
const DEPOSIT_RIM_W = 1.2;
/** How many segments a dashed circle is sampled into. */
const DEPOSIT_RING_STEPS = 48;

/** How many chips a commodity wears: one alloy, two biomass, three above. */
export function depositChips(resource: number): number {
  return Math.min(DEPOSIT_CHIPS.length, Math.max(1, resource));
}

/** Lays the chips of a commodity as sub-paths of one path. The caller paints. */
function chipPaths(g: MarkGraphics, resource: number): void {
  const chips = depositChips(resource);
  for (let i = 0; i < chips; i++) g.poly(DEPOSIT_CHIPS[i]);
}

/** The colour of what is in the ground, with the old fallback (R475). */
export function depositInk(resource: number): number {
  return resourceOf(resource)?.color ?? 0xd8c66a;
}

/**
 * The letter card 08 writes: the first letter of the commodity's name.
 *
 * Alloy is A, biomass is B and volatiles is V, which are the three a season
 * map puts in the ground. An arcade map that names another commodity takes
 * that commodity's own first letter, and one the client does not know at
 * all writes a question mark rather than nothing.
 */
export function depositLetter(resource: number): string {
  return (resourceOf(resource)?.name ?? '?').charAt(0).toUpperCase();
}

/**
 * The cluster of the AUTO mark, at the three alphas a card asks for.
 *
 * `fillA` is the diamond, `chipA` the chips and `rimA` the rim. An alpha of
 * zero leaves that part out, which is how card 06 draws an outline with no
 * body. The chips are ONE paint, as they are in `drawDeposit` (§10.1).
 */
function depositCluster(
  g: MarkGraphics, resource: number, fillA: number, chipA: number, rimA: number,
  ink: number, rim: number,
): void {
  if (fillA > 0) g.poly(DIAMOND).fill({ color: ink, alpha: fillA });
  if (chipA > 0) {
    chipPaths(g, resource);
    g.fill({ color: ink, alpha: chipA });
  }
  if (rimA > 0) {
    g.poly(DIAMOND).stroke({ width: DEPOSIT_RIM_W, color: rim, alpha: rimA });
  }
}

/** The solid cluster a stocked field wears under cards 03, 05 and 06. */
function solidCluster(g: MarkGraphics, resource: number, c: number, chipA: number): void {
  depositCluster(g, resource, 1, chipA, 1, c, brighten70(c));
}

/** The quiet cluster a worked-out field wears, at `a` of its own ink. */
function quietCluster(g: MarkGraphics, resource: number, c: number, a: number): void {
  const f = DEPOSIT_MARKS;
  depositCluster(
    g, resource, f.quietFillAlpha * a, f.quietChipAlpha * a, f.quietRimAlpha * a,
    c, brighten70(c),
  );
}

// ---------------------------------------------------------------------------
// The point samplers
// ---------------------------------------------------------------------------
//
// `MarkGraphics` carries no curve call, so a card's `quadraticCurveTo` and
// `bezierCurveTo` are stated as straight pieces of a `poly`. Every mark
// module samples through these two, so one curve is one arithmetic.

/**
 * One quadratic curve, sampled into the point list a `poly` is built from.
 *
 * `out` already holds the start point; the `steps` points after it are
 * pushed, the last of them the end point.
 */
export function quadratic(
  out: number[], x0: number, y0: number, cx: number, cy: number,
  x1: number, y1: number, steps = 12,
): void {
  for (let i = 1; i <= steps; i++) {
    const t = i / steps, u = 1 - t;
    out.push(u * u * x0 + 2 * u * t * cx + t * t * x1,
      u * u * y0 + 2 * u * t * cy + t * t * y1);
  }
}

/** One cubic curve, sampled the same way: `quadratic` with two control points. */
export function cubic(
  out: number[], x0: number, y0: number, c0x: number, c0y: number,
  c1x: number, c1y: number, x1: number, y1: number, steps = 12,
): void {
  for (let i = 1; i <= steps; i++) {
    const t = i / steps, u = 1 - t;
    const a = u * u * u, b = 3 * u * u * t, c = 3 * u * t * t, d = t * t * t;
    out.push(a * x0 + b * c0x + c * c1x + d * x1,
      a * y0 + b * c0y + c * c1y + d * y1);
  }
}

/** The unit corners of a pointy-top hex at -30°, 30°, 90°, …, the order
 *  `world.hexCorner` walks. Computed once, so a per-cell caller pays no
 *  trig on a frame. */
const HEX_CORNER_X = new Float64Array(6);
const HEX_CORNER_Y = new Float64Array(6);
for (let k = 0; k < 6; k++) {
  const a = ((60 * k - 30) * Math.PI) / 180;
  HEX_CORNER_X[k] = Math.cos(a);
  HEX_CORNER_Y[k] = Math.sin(a);
}

/** What `hexPath` writes to: a 2D canvas context or a `MarkGraphics`. */
export interface HexPathSink {
  moveTo(x: number, y: number): unknown;
  lineTo(x: number, y: number): unknown;
  closePath(): unknown;
}

/**
 * One pointy-top hex of radius `r` about (`x`, `y`), as a closed sub-path
 * of the current path. It begins no path of its own, so a caller lays many
 * cells into one path and fills them once (§10.1). The menu previews, the
 * arcade deck and the symbols page trace their cells through it.
 */
export function hexPath(c: HexPathSink, x: number, y: number, r: number): void {
  c.moveTo(x + HEX_CORNER_X[0] * r, y + HEX_CORNER_Y[0] * r);
  for (let k = 1; k < 6; k++) c.lineTo(x + HEX_CORNER_X[k] * r, y + HEX_CORNER_Y[k] * r);
  c.closePath();
}

/**
 * One pointy-top hex of radius `r` about the origin as a point list, a
 * vertex at the top, in the corner order of `hexPath`. The badges and the
 * tile structure set draw their hexes from it.
 */
export function hexPoints(r: number): number[] {
  const p: number[] = [];
  for (let k = 0; k < 6; k++) p.push(HEX_CORNER_X[k] * r, HEX_CORNER_Y[k] * r);
  return p;
}

/**
 * A regular star as one closed polygon about the origin: `n` points between
 * two radii, the first at angle `rot`.
 */
export function starPoints(n: number, ro: number, ri: number, rot = 0): number[] {
  const p: number[] = [];
  for (let k = 0; k < n * 2; k++) {
    const a = rot + k * Math.PI / n;
    const r = k % 2 ? ri : ro;
    p.push(Math.cos(a) * r, Math.sin(a) * r);
  }
  return p;
}

/**
 * A rounded rectangle as a point list, corners sampled as quadratics.
 *
 * `MarkGraphics` has no `roundRect`, so the corner is sampled the way
 * `hold-marks.ts` samples its flare. Four steps are enough at a corner of
 * three map px, which is the only size the map draws.
 */
export function roundedRect(
  x: number, y: number, w: number, h: number, r: number, steps = 4,
): number[] {
  const pts: number[] = [];
  const corner = (x0: number, y0: number, cx: number, cy: number, x1: number, y1: number) => {
    pts.push(x0, y0);
    quadratic(pts, x0, y0, cx, cy, x1, y1, steps);
  };
  corner(x + r, y, x, y, x, y + r);
  corner(x, y + h - r, x, y + h, x + r, y + h);
  corner(x + w - r, y + h, x + w, y + h, x + w, y + h - r);
  corner(x + w, y + r, x + w, y, x + w - r, y);
  return pts;
}

/** A circle as a point list, for a caller that must dash it. */
function ringPoints(r: number, steps = DEPOSIT_RING_STEPS): number[] {
  const pts: number[] = [];
  for (let i = 0; i < steps; i++) {
    const a = (i / steps) * Math.PI * 2;
    pts.push(Math.cos(a) * r, Math.sin(a) * r);
  }
  return pts;
}

/** A rest of a run shorter than this, in px, is no run (R2240). */
const DASH_EPS = 1e-9;

/**
 * A dashed outline, as `on`/`off` runs of `moveTo` and `lineTo`: the ONE
 * dash walker of the map marks and the hold marks (R2240).
 *
 * `MarkGraphics` has no `setLineDash`, because `GraphicsContext` had none:
 * a dash is geometry there, not a stroke state. The walk is a toggle: it
 * draws `on`, skips `off`, and carries the part of the run in hand across
 * a corner, so the pattern goes on round the outline and two joined dashes
 * never read as one long one. The phase starts again at 0 at each toggle,
 * and a rest under `DASH_EPS` ends a run, so no float drift draws a sliver
 * or stalls the walk. `close` walks the edge back to the first point too.
 *
 * The caller paints. Every run is a sub-path of one path, so a dashed
 * outline costs ONE stroke whatever it is made of (§10.1).
 */
export function dashedPoly(
  g: MarkGraphics, pts: number[], on: number, off: number, close = true,
): void {
  const n = pts.length / 2;
  if (n < 2 || !(on >= 0) || !(off >= 0) || !(on + off > 0)) return;
  let drawing = true;
  let phase = 0;
  const last = close ? n : n - 1;
  for (let i = 0; i < last; i++) {
    const x0 = pts[i * 2], y0 = pts[i * 2 + 1];
    const j = (i + 1) % n;
    const x1 = pts[j * 2], y1 = pts[j * 2 + 1];
    const len = Math.hypot(x1 - x0, y1 - y0);
    if (!(len > 0)) continue;
    let done = 0;
    while (done < len - DASH_EPS) {
      const span = drawing ? on : off;
      const step = Math.min(span - phase, len - done);
      if (drawing && step > 0) {
        const a = done / len, b = (done + step) / len;
        g.moveTo(x0 + (x1 - x0) * a, y0 + (y1 - y0) * a);
        g.lineTo(x0 + (x1 - x0) * b, y0 + (y1 - y0) * b);
      }
      done += step;
      phase += step;
      if (phase >= span - DASH_EPS) { drawing = !drawing; phase = 0; }
    }
  }
}

/**
 * Card 03 "Strike": a worked-out field is crossed out.
 *
 * A stocked field is the solid cluster and nothing else. A worked-out one
 * drops to `empty_alpha` of the quiet cluster and takes a bar straight
 * through its middle in the strike ink. The bar is a SHAPE and not a fade,
 * so a sovereignty tint under the mark takes nothing away from it (R475).
 */
export function drawDepositStrike(g: MarkGraphics, resource: number, level: number): void {
  const c = depositInk(resource);
  const f = DEPOSIT_MARKS.strike;
  if (level > 0) {
    solidCluster(g, resource, c, DEPOSIT_MARKS.solidChipAlpha);
    return;
  }
  quietCluster(g, resource, c, f.emptyAlpha);
  g.moveTo(-f.barHalf, 0).lineTo(f.barHalf, 0).stroke({
    width: f.barW, color: DEPOSIT_MARKS.strikeInk, alpha: f.barAlpha, cap: 'butt',
  });
}

/**
 * Card 05 "Gauge": a ring says how much is left.
 *
 * The cluster stays solid, and a ring around it carries the stock: a faint
 * track over the whole circle, and a clockwise arc from twelve o'clock to
 * `level / DEPOSIT_LEVELS` of it. A worked-out field turns the ring into a
 * dotted circle in the ghost ink and drops the cluster to `empty_alpha`.
 *
 * The arc starts with a `moveTo` at twelve o'clock, because an `arc()` from
 * a current point draws a line to the start of the arc — the rule this
 * file's header states.
 */
export function drawDepositGauge(g: MarkGraphics, resource: number, level: number): void {
  const c = depositInk(resource);
  const f = DEPOSIT_MARKS.gauge;
  if (level <= 0) {
    quietCluster(g, resource, c, f.emptyAlpha);
    dashedPoly(g, ringPoints(f.ringR), f.emptyDashOn, f.emptyDashOff);
    g.stroke({
      width: f.emptyW, color: DEPOSIT_MARKS.ghostInk, alpha: f.emptyRingAlpha, cap: 'butt',
    });
    return;
  }
  solidCluster(g, resource, c, DEPOSIT_MARKS.solidChipAlpha);
  g.circle(0, 0, f.ringR).stroke({ width: f.ringW, color: c, alpha: f.trackAlpha });
  const step = Math.min(level, DEPOSIT_LEVELS) / DEPOSIT_LEVELS;
  const top = -Math.PI / 2;
  g.moveTo(0, -f.ringR).arc(0, 0, f.ringR, top, top + Math.PI * 2 * step).stroke({
    width: f.ringW, color: c, alpha: f.arcAlpha, cap: 'butt',
  });
}

/**
 * Card 06 "Ghost": colour is the stock.
 *
 * A stocked field is a solid cluster over a soft halo of its own colour.
 * The halo is a radial gradient, so the atlas paints it under this
 * description and the description itself draws no halo at all.
 *
 * A worked-out field loses its colour: the diamond and every chip become
 * one dashed grey outline. The chip count still says the commodity, which
 * is the rule R475 added to the drain.
 */
export function drawDepositGhost(g: MarkGraphics, resource: number, level: number): void {
  const c = depositInk(resource);
  const f = DEPOSIT_MARKS.ghost;
  if (level > 0) {
    solidCluster(g, resource, c, f.chipAlpha);
    return;
  }
  dashedPoly(g, DIAMOND, f.emptyDashOn, f.emptyDashOff);
  const chips = depositChips(resource);
  for (let i = 0; i < chips; i++) {
    dashedPoly(g, DEPOSIT_CHIPS[i], f.emptyDashOn, f.emptyDashOff);
  }
  g.stroke({
    width: DEPOSIT_CHIP_W, color: DEPOSIT_MARKS.ghostInk, alpha: f.emptyAlpha, cap: 'butt',
  });
}

/**
 * Card 08 "Tag": a letter says the commodity.
 *
 * A stocked field is a solid tile in its colour with a lit rim; a
 * worked-out field is the tile's outline alone. The LETTER is text, so the
 * atlas writes it over this description in the vendored chart face (R548)
 * and the description draws the tile only.
 *
 * The mark carries no diamond and no chips: a letter reads at 1:1 where a
 * 4 px chip does not, in colour and in grey.
 */
export function drawDepositTag(g: MarkGraphics, resource: number, level: number): void {
  const c = depositInk(resource);
  const f = DEPOSIT_MARKS.tag;
  const half = f.size / 2;
  const tile = roundedRect(-half, -half, f.size, f.size, f.corner);
  if (level > 0) {
    g.poly(tile).fill({ color: c, alpha: 1 });
    g.poly(tile).stroke({ width: DEPOSIT_CHIP_W, color: brighten70(c), alpha: f.rimAlpha });
    return;
  }
  g.poly(tile).stroke({ width: DEPOSIT_CHIP_W, color: c, alpha: f.emptyAlpha });
}

/**
 * Card 10 "Tally": the chips become the mark.
 *
 * The chip rule is right and the chips are too small, so the count IS the
 * mark: one bar for alloy, two for biomass, three for volatiles, side by
 * side and no diamond. A stocked bar is solid with a lit rim; a worked-out
 * bar is hollow. The two states are fill against outline, which is the rule
 * of R475, and the count reads at 1:1 in grey.
 */
export function drawDepositTally(g: MarkGraphics, resource: number, level: number): void {
  const c = depositInk(resource);
  const f = DEPOSIT_MARKS.tally;
  const n = depositChips(resource);
  const pitch = f.barW + f.gap;
  const x0 = -(n * f.barW + (n - 1) * f.gap) / 2;
  const bars = () => {
    for (let i = 0; i < n; i++) {
      g.poly(roundedRect(x0 + i * pitch, -f.barH / 2, f.barW, f.barH, f.corner));
    }
  };
  bars();
  if (level <= 0) {
    g.stroke({ width: f.emptyW, color: c, alpha: f.emptyAlpha });
    return;
  }
  g.fill({ color: c, alpha: 1 });
  bars();
  g.stroke({ width: f.rimW, color: brighten70(c), alpha: f.rimAlpha });
}

/** One deposit description: the field's commodity and its fill step. */
export type DepositDraw = (g: MarkGraphics, resource: number, level: number) => void;

/**
 * The six values of the `deposit` field, each with its description.
 *
 * The record is exhaustive over `DepositStyle`, so a value added to
 * `labels.toml` `deposit_styles` is a type error here until it draws.
 */
export const DEPOSIT_STYLE_DEFS: Record<DepositStyle, DepositDraw> = {
  drain: drawDeposit,
  strike: drawDepositStrike,
  gauge: drawDepositGauge,
  ghost: drawDepositGhost,
  tag: drawDepositTag,
  tally: drawDepositTally,
};

/**
 * The mark of one field, under the value the reader's Marks theme picked.
 *
 * A value this build does not carry falls back to the AUTO drain, the way
 * every look resolver reads a field it does not know (themes.md §12).
 */
export function drawDepositStyle(
  g: MarkGraphics, style: DepositStyle, resource: number, level: number,
): void {
  (DEPOSIT_STYLE_DEFS[style] ?? drawDeposit)(g, resource, level);
}

// ---------------------------------------------------------------------------
// Map-author marks (arcade.md §4.9)
// ---------------------------------------------------------------------------
//
// A marker is an annotation an arcade map author drops on a tile, so a mode
// can say "this tile is the objective". It carries no gameplay at all: the
// server sends the list one time in the hello and the sim never reads it.
//
// The two shapes paint their own ink, like the site marks above and unlike
// the hull descriptions, because a marker's colour is the author's choice
// and no caller re-decides it.

/**
 * An arrow, pointing along its own local +x axis. The caller rotates the
 * node to the heading, exactly as `fleets.ts` rotates a hull.
 *
 * It is the `needle` the support icons are built on, at map-mark scale, so
 * an author's arrow reads as kin to the hull marks without being one.
 */
export function drawMarkerArrow(g: MarkGraphics, color: number) {
  needle(g, 20, 7);
  g.fill({ color, alpha: 0.85 });
  needle(g, 20, 7);
  g.stroke({ width: 1.5, color: brighten70(color), alpha: 0.9, join: 'round' });
}

/** A ring, the same family as `drawCP` with the centre dot left out. */
export function drawMarkerCircle(g: MarkGraphics, color: number) {
  g.circle(0, 0, 13).stroke({ width: 2.5, color, alpha: 0.85 });
  g.circle(0, 0, 13).fill({ color, alpha: 0.12 });
}

/** A pale rim of the same hue, so the outline reads as light on the fill. */
export function brighten70(color: number): number {
  const r = Math.min(255, ((color >> 16) & 0xff) + 70);
  const g = Math.min(255, ((color >> 8) & 0xff) + 70);
  const b = Math.min(255, (color & 0xff) + 70);
  return (r << 16) | (g << 8) | b;
}

/**
 * A colour moved a fraction `k` of the way to white (R830).
 *
 * `brighten70` adds a fixed 70 to every channel, which is what a rim of the
 * same hue needs. This one is a ratio instead, so a dark ink and a bright
 * ink move by the same PROPORTION of what is left to white and the two
 * results read as the same step. The squad `tint-only` value is the caller:
 * a squadmate's hull stands a quarter of the way from its faction colour to
 * the white of an own hull.
 */
export function lift(color: number, k: number): number {
  return mixColor(color, 0xffffff, k);
}

// ---------------------------------------------------------------------------
// Hull marks (ui-map-interface.md §6.1)
// ---------------------------------------------------------------------------

/** A straight bar of width `w` from (x0,y0) to (x1,y1) — the stroke every
 * filigree icon is built from. Pixi has no stroked-path fill that survives
 * texture generation cleanly, so bars are quads. */
export function bar(g: MarkGraphics, x0: number, y0: number, x1: number, y1: number, w: number) {
  const dx = x1 - x0, dy = y1 - y0;
  const len = Math.hypot(dx, dy) || 1;
  const nx = (-dy / len) * (w / 2), ny = (dx / len) * (w / 2);
  g.poly([x0 + nx, y0 + ny, x1 + nx, y1 + ny, x1 - nx, y1 - ny, x0 - nx, y0 - ny]);
}

/** An open arc band between two radii — an arc drawn as area, not as stroke. */
export function band(g: MarkGraphics, x: number, y: number, ro: number, ri: number, a0: number, a1: number) {
  g.moveTo(x + Math.cos(a0) * ro, y + Math.sin(a0) * ro);
  g.arc(x, y, ro, a0, a1);
  g.arc(x, y, ri, a1, a0, true);
  g.closePath();
}

/**
 * A closed ring drawn as area: an outer circle one way and an inner circle
 * the other, so the nonzero fill rule leaves the hole.
 *
 * Each circle states its own `moveTo` and closes, the way `circle()` of the
 * shim does, so the ring is two sub-paths of its own and joins to nothing
 * before it. One description therefore gives both masters: the filled master
 * takes the annulus, and the hollow master strokes two circles.
 */
export function ring(g: MarkGraphics, x: number, y: number, ro: number, ri: number) {
  g.moveTo(x + ro, y);
  g.arc(x, y, ro, 0, Math.PI * 2, false);
  g.closePath();
  g.moveTo(x + ri, y);
  g.arc(x, y, ri, 0, Math.PI * 2, true);
  g.closePath();
}

/** The slim directional core the filigree shapes hang off. */
export function needle(g: MarkGraphics, len: number, halfW: number) {
  g.poly([len, 0, -6, halfW, -4.6, 0, -6, -halfW]);
}

/**
 * One hull silhouette, as paths only. A description lays down its paths and
 * says nothing about ink: the master builder decides whether to fill them
 * (your own hull and the faction's) or to stroke them (a clanmate's, card
 * B05), which is how one description gives both masters.
 *
 * Mind the file's rule: in Pixi v8 a `fill()` applies to every path since
 * the last fill on the same Graphics, so a description must never call one.
 */
export type HullPath = (g: MarkGraphics) => void;

/**
 * The combat dart: the mark of a contact whose class the server withheld
 * (95 px² of ink).
 *
 * Every combat silhouette below is this shape plus two wings, so a warship
 * is a warship at first read and the class is the second read. The dart
 * alone is what a contact wears while no scan holds it (R426).
 */
export const dartPath: HullPath = (g) => {
  g.poly([14, 0, -8, 5, -5, 0, -8, -5]);
};

/** The length of the one support needle, in map px (card 01, R835). */
const SUPPORT_NEEDLE_LEN = 13;
/** The half width of the one support needle, in map px (R835). */
const SUPPORT_NEEDLE_HALF_W = 1.6;

/**
 * The base of every support silhouette: one needle, and the same one for all
 * seven classes (mockup round thirty-seven card 01, R835).
 *
 * Before R835 the Sentinel stood on a dart of her own and the Oracle's needle
 * was 10 long. The owner asked for one rule, so both now stand here.
 */
export function supportBase(g: MarkGraphics): void {
  needle(g, SUPPORT_NEEDLE_LEN, SUPPORT_NEEDLE_HALF_W);
}

/**
 * What each support class wears ON the needle (card 01, R835).
 *
 * The table holds the EXTRAS alone, so `SUPPORT_ICONS` below and the eleven
 * alternative sets of `hull-styles.ts` all read one source: three of those
 * sets keep a card 01 support mark for a class of their own.
 *
 * Sentinel and Vedette are round five
 * (`archive/mockups/005-support-fleet-icons.html`); Oracle and Dredger are
 * round six (`006-support-fleet-icons-filigree.html`), which redrew them after
 * the first three read as too massive beside the first two. The Tender is
 * round thirty-three (`033-tender-icons.html`), which gave the class a shape
 * of its own after R712 shipped it under a temporary one. The Beacon is round
 * thirty-five (`035-beacon-icons.html`), which put the spoked core on a needle
 * and replaced round six's Radial Fine.
 */
export const SUPPORT_EXTRAS: [ShipClass, HullPath][] = [
  // Sentinel — Aegis Wing (round five): two detached swept flanking bars.
  // The bars are the one support mark that reads as a weapon, because it is
  // the one support hull that shoots.
  [ShipClass.SHIP_CLASS_SENTINEL, (g) => {
    g.poly([-6.9, -7.6, 3.1, -5.6, 3.5, -3.4, -6.5, -5.4]);
    g.poly([-6.9, 7.6, 3.1, 5.6, 3.5, 3.4, -6.5, 5.4]);
  }],
  // Vedette — Feather (round five): three quills astern. The quietest shape
  // on the board, and it survives the smallest sizes.
  [ShipClass.SHIP_CLASS_VEDETTE, (g) => {
    g.poly([-4.2, -0.5, -9.6, -3.9, -10.4, -2.9, -5, 0.5]);
    g.poly([-5.6, -0.55, -11.5, -0.55, -11.5, 0.55, -5.6, 0.55]);
    g.poly([-4.2, 0.5, -9.6, 3.9, -10.4, 2.9, -5, -0.5]);
  }],
  // Oracle — Open Arcs (round six): two thin open arcs ahead of the needle,
  // nothing filled — the sonar picture without the mass that made Ripple the
  // heaviest marker on the roster. Card 01 moved the two arcs out to 11.4 and
  // 15.2, so the longer needle still reaches between them.
  [ShipClass.SHIP_CLASS_ORACLE, (g) => {
    band(g, 1, 0, 11.4, 10.4, -0.85, 0.85);
    band(g, 1, 0, 15.2, 14.2, -0.85, 0.85);
  }],
  // Dredger — Tow Line (round six): two open crates on a line astern. Cargo
  // as a countable thing rather than a filled hold, and the tow reads the
  // heading as clearly as the prow does.
  [ShipClass.SHIP_CLASS_DREDGER, (g) => {
    bar(g, -5, 0, -13, 0, 1.1);
    for (const x of [-6.6, -11.2]) {
      bar(g, x, -2.8, x, 2.8, 1.0);
      bar(g, x - 2.2, -2.8, x - 2.2, 2.8, 1.0);
      bar(g, x, -2.8, x - 2.2, -2.8, 1.0);
      bar(g, x, 2.8, x - 2.2, 2.8, 1.0);
    }
  }],
  // Tender — The lifebuoy (round thirty-three card 05, R724): a ring
  // amidships, which the needle runs through. The rescue ring is what a hull
  // that saves other hulls wears, and it is the one closed circle on any hull
  // mark, so the class reads at a glance. It replaces the temporary Derrick
  // of R712.
  [ShipClass.SHIP_CLASS_TENDER, (g) => {
    ring(g, -2, 0, 6, 4.6);
  }],
  // Beacon — The radial on the needle (round thirty-five card 01, R727): the
  // spoked core of Radial Fine, carried aft onto the needle. The long forward
  // spoke of round six became the needle, and the core moved to (−4, 0) so
  // the bow leads; seven short spokes remain, and the eighth is the needle
  // itself. It says what the old mark said, a source that radiates all round,
  // and the heading now reads from the needle first. Round six called the old
  // heading the weakest of its pick.
  [ShipClass.SHIP_CLASS_BEACON, (g) => {
    g.circle(-4, 0, 2.2);
    for (let i = 1; i < 8; i++) {
      const a = (i * Math.PI) / 4;
      bar(g, -4 + Math.cos(a) * 2.8, Math.sin(a) * 2.8, -4 + Math.cos(a) * 6.6, Math.sin(a) * 6.6, 1.1);
    }
  }],
  // Sapper — Mine Rails (round sixty-seven card 01, v2-the-deep.md §11.8,
  // R2652): two thin rails run aft past the stern, and one sea mine has
  // just left their ends. No mark of the row has two parallel bars astern.
  // The rails stand clear of the needle, so their winding cuts nothing.
  [ShipClass.SHIP_CLASS_SAPPER, (g) => {
    bar(g, -3, 2.4, -10.6, 2.4, 1.05);
    bar(g, -3, -2.4, -10.6, -2.4, 1.05);
    g.circle(-13.6, 0, 1.6);
  }],
];

/**
 * The seven support silhouettes of the AUTO ship icon set: the one needle,
 * and the extras of the class on it (card 01, R835; the Sapper is round
 * sixty-seven card 01, R2652).
 *
 * Each is drawn white and tinted per faction, in the same coordinate space as
 * the combat dart (`poly([14,0, -8,5, -5,0, -8,-5])`, about 22 x 10 px), and
 * each rotates to heading like every other marker.
 *
 * The weight they are held to is measured, not judged: **ink**, a marker's
 * filled area in px² at map scale. Card 01 measures the six at Sentinel 70,
 * Vedette 47, Oracle 71, Tender 73, Beacon 64 and Dredger 64 (R1540), so the Tender
 * is the heaviest support mark. The Vedette quills wind against the needle, so
 * their overlap at the stern cuts 2 px² (the page reads 49); the owner keeps
 * that look (R1540). The Sapper's Mine Rails add 53. Every
 * combat mark stays above all seven.
 *
 * The combat classes have silhouettes of their own in `COMBAT_ICONS` below
 * (R426). A dart on the map means "class withheld", and any silhouette,
 * support or combat, means "class given".
 */
export const SUPPORT_ICONS: [ShipClass, HullPath][] = SUPPORT_EXTRAS.map(
  ([cls, extras]) => [cls, (g: MarkGraphics) => { supportBase(g); extras(g); }],
);

/**
 * The two wings of a combat silhouette: one poly a side, the second the
 * first mirrored about the hull axis.
 *
 * A wing is stated one time, for the +y side, and this draws it twice. The
 * two halves therefore cannot drift apart, and a silhouette reads as one
 * shape and not as two that happen to match.
 *
 * The mirror reverses the point order as well (R835). A mirror alone turns
 * the winding round, and a wing dips inside the dart near its root, so the
 * nonzero fill cut a notch of about one px² from the port wing of every
 * combat mark and none from the starboard one. Both wings now wind with
 * the dart, and the two halves are true mirrors on the water.
 */
function wings(g: MarkGraphics, p: readonly number[]) {
  g.poly([...p]);
  const m: number[] = [];
  for (let i = p.length - 2; i >= 0; i -= 2) m.push(p[i], -p[i + 1]);
  g.poly(m);
}

/**
 * The six combat silhouettes: the WINGS family of mockup round twenty-three,
 * card 03 (`archive/mockups/023-combat-hulls.html`), which the owner picked on
 * 2026-09-01 (R426).
 *
 * Each one is the combat dart with two attached wings, drawn in the same
 * coordinate space as the dart and the five support icons above, white and
 * tinted per faction by the sprite that carries it.
 *
 * **The rule the family states.** The sweep of a wing is the ROLE and its
 * span is the WEIGHT:
 *
 *   - swept back for a brawler (Cutlass, Bombard);
 *   - far back for a skirmisher (Corsair);
 *   - straight for artillery (Falconet, Basilisk);
 *   - forward for the torpedo boat (Lancer);
 *   - span grows with weight — W4 reaches 8, W6 reaches 9, W10 and W12
 *     reach 11.
 *
 * **The ink**, in px² at map scale, measured the way the mockup page
 * measures it: rasterise at 8x and count the covered samples. The dart is
 * 95 and the Tender is the heaviest support mark at 73 (R835). Corsair 122,
 * Cutlass 138, Falconet 124, Basilisk 153, Bombard 181, Lancer 122. The
 * Bombard is the heaviest mark on the water, which is what the heaviest
 * hull in the game should be.
 *
 * **The accepted collision** (R426): the Sentinel also carries a pair of
 * bars beside its hull. Hers are DETACHED and parallel; these are ATTACHED
 * deltas that touch the dart. The owner accepted the risk.
 */
export const COMBAT_WINGS: [ShipClass, readonly number[]][] = [
  // Corsair — skirmish, W4 (122 px²): a small wing far back on the tail.
  [ShipClass.SHIP_CLASS_CORSAIR, [0, 3.8, -6, 8, -8.5, 8, -4, 3.8]],
  // Cutlass — brawler, W4 (138 px²): the same span swept back off the beam.
  [ShipClass.SHIP_CLASS_CUTLASS, [2.5, 4, -3, 9, -6.5, 9, -2.5, 4]],
  // Falconet — artillery, W6 (124 px²): a straight wing square to the axis.
  [ShipClass.SHIP_CLASS_FALCONET, [1.5, 3.8, 1.5, 9, -1.2, 9, -1.2, 3.8]],
  // Basilisk — artillery, W10 (153 px²): the Falconet's wing, reaching to 11.
  [ShipClass.SHIP_CLASS_BASILISK, [2.5, 3.8, 2.5, 11, -1.5, 11, -1.5, 3.8]],
  // Bombard — brawler, W12 (181 px²): a broad wing swept back to 11.
  [ShipClass.SHIP_CLASS_BOMBARD, [4, 4.2, -4, 11, -9, 11, -4, 4.4]],
  // Lancer — torpedo, W6 (122 px²): the one wing that sweeps FORWARD.
  [ShipClass.SHIP_CLASS_LANCER, [-2.5, 4.2, 4, 9, 1.2, 9, -5, 4.6]],
];

/**
 * The Commodore, the command ship (v2-the-deep.md §16.7, R2550): mockup
 * round 108 card 03 Forked Wings with the star of card 02 Command Star
 * added astern. The owner wrote "Use 03 with the star from 02 added".
 *
 * The wing is a swept wing of span 10.2 whose tip ends in a swallow tail,
 * the broad pennant of a commodore, so the mark stays in the WINGS family.
 * The solid five-point star stands clear of the stern, 13 px astern. It is
 * the one combat mark with a part behind the stern, so it reads apart from
 * the Cutlass when the notch of the fork closes at 0.5×.
 */
const COMMODORE_WING: readonly number[] = [2.8, 4, -2.4, 9.8, -4.4, 7.4, -7.8, 10.2, -2.8, 4.1];
const COMMAND_STAR: readonly number[] = starPoints(5, 4.3, 1.8)
  .map((v, k) => (k % 2 === 0 ? v - 13 : v));

/**
 * The seven combat silhouettes: the dart, and the wings of the class on
 * it. The Commodore adds its star, so it draws four polygons.
 */
export const COMBAT_ICONS: [ShipClass, HullPath][] = [
  ...COMBAT_WINGS.map(
    ([cls, wing]): [ShipClass, HullPath] => [cls, (g: MarkGraphics) => { dartPath(g); wings(g, wing); }],
  ),
  [ShipClass.SHIP_CLASS_COMMODORE, (g: MarkGraphics) => {
    dartPath(g);
    wings(g, COMMODORE_WING);
    g.poly([...COMMAND_STAR]);
  }],
];
